import type { PropsWithChildren } from 'react';
import * as Sentry from '@sentry/react';

import { ErrorModal } from '@/ui';

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV ?? 'development',
  enabled: import.meta.env.VITE_ENV !== 'development',

  // Performance Monitoring
  tracesSampleRate: 1.0,

  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Privacy: Don't send user IP addresses by default
  sendDefaultPii: false,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  beforeSend: event => {
    if (import.meta.env.VITE_ENV === 'development') {
      return null;
    }
    return event;
  },
});

export const SentryProvider = ({ children }: PropsWithChildren) => {
  return <>{children}</>;
};

export const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: PropsWithChildren) => <>{children}</>,
  {
    fallback: ({ error, resetError }) => (
      <ErrorModal
        onClose={resetError}
        onAction={() => window.location.replace('/')}
      />
    ),
    beforeCapture: (scope, error, errorInfo) => {
      scope.setTag('errorBoundary', true);
      scope.setLevel('error');
      if (errorInfo) {
        scope.setContext('errorInfo', { errorInfo });
      }
    },
  },
);
